/**
 * BXI-admin: Proforma invoice (PI) view – same layout as user-side PI:
 * product table, Additional Cost (Packaging, Logistics, BXI Commission), Total before Tax,
 * Terms & Conditions, GST summary, Tax breakdown table.
 */

import { formatHsnForDisplay } from "../../utils/formatHsnForDisplay";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { formatCommissionPercentSuffix } from "../AllRecords/Allinvoicedetail/invoiceCommissionPercent";
import { toast } from "react-toastify";
import { getToken } from "../../common/ApiUtility";
import CommaSeparator from "../../common/CommaSeparator";
import NumberToWord from "../../common/NumberToWord";
import PICompanyHeader from "./PICompanyHeader";
import PIInfoRow from "./ProformaInvoice/PIInfoRow";
import PISellerBuyerDetails from "./ProformaInvoice/PISellerBuyerDetails";
import {
  buildInvoiceLogisticsAndIncidentalCharges,
  buildMediaBrandWorldCommissionCharge,
  formatMediaTimelineLabel,
  getMediaProductAdditionalCostRows,
  invoiceEffectiveIsIgst,
  isMediaBrandWorldAggregatorPI,
  mediaBrandWorldProductLineTaxableExGst,
  resolveMediaPiBoughtSeconds,
  resolveMediaPiHsn,
} from "./mediaBrandWorldPiCalc";
import PIActionBar from "./PIActionBar";
import { pickMediaPiCols } from "./mediaPiColumns";


const BxiCoin =
  "https://bxidevelopment1.s3.ap-south-1.amazonaws.com/assets/Images/CommonImages/BXIToken.svg";

const CommongTextStyle = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "12px",
  color: "rgba(80,80,80,1)",
};

function normalizeOrderRequestForId(res, routeOrderRequestId) {
  const data = res?.data?.data;
  const hints = res?.data?.sellerFlowHints || {};
  if (!data) return null;
  if (data.peerListingGroupId && data.part1) {
    const oid = String(routeOrderRequestId);
    if (data.part1?._id != null && String(data.part1._id) === oid) {
      return { ...data.part1, sellerFlowHints: hints };
    }
    if (data.part2?._id != null && String(data.part2._id) === oid) {
      return { ...data.part2, sellerFlowHints: hints };
    }
    return { ...data.part1, sellerFlowHints: hints };
  }
  return { ...data, sellerFlowHints: hints };
}

export default function MediaProformaInvoiceForBuyer() {
  const { id, orderId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderRequestForPay, setOrderRequestForPay] = useState(null);
  const [orderRequestLoading, setOrderRequestLoading] = useState(false);
  const [payTcLoading, setPayTcLoading] = useState(false);
  const [acceptPiLoading, setAcceptPiLoading] = useState(false);
  const [dataGridrows, setDataGridRows] = useState([]);
  const downloadRef = useRef(null);
  const componentRef = useRef();

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    const token = getToken();
    axios
      .get(`api/v1/admin/get-invoice/${id}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        setInvoice(res.data ?? null);
        setError(null);
      })
      .catch((err) => {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load invoice",
        );
        setInvoice(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const resolvedOrderId =
    orderId || invoice?.orderRequestId || invoice?.OrderSummeryId || null;

  useEffect(() => {
    if (invoice) {
      setDataGridRows(invoice?.ProductData?.at(0)?.mediaLineItems);
    }
  }, [invoice]);

  useEffect(() => {
    if (!resolvedOrderId) {
      setOrderRequestForPay(null);
      return;
    }
    const token = getToken();
    setOrderRequestLoading(true);
    axios
      .get(`aggregator/order-request/${resolvedOrderId}`, {
        params: { view: "admin" },
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        setOrderRequestForPay(normalizeOrderRequestForId(res, resolvedOrderId));
      })
      .catch(() => setOrderRequestForPay(null))
      .finally(() => setOrderRequestLoading(false));
  }, [resolvedOrderId]);

  const payTcGrandTotal =
    orderRequestForPay?.totals?.grandTotalBXI != null
      ? Number(orderRequestForPay.totals.grandTotalBXI)
      : null;
  const showProcurementPayTc =
    Boolean(resolvedOrderId) &&
    orderRequestForPay &&
    orderRequestForPay.isAdminInitiated === true &&
    orderRequestForPay.status === "buyer_accepted_pi" &&
    !orderRequestForPay.paymentData?.walletOnHoldCreatedAt &&
    (Number(orderRequestForPay.totals?.grandTotalBXI) || 0) > 0;

  const handlePayProcurementTC = useCallback(async () => {
    if (!resolvedOrderId) return;
    setPayTcLoading(true);
    try {
      const token = getToken();
      await axios.post(
        `inventory/order-request/${resolvedOrderId}/pay-tc`,
        {},
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      toast.success(
        "TC debited from BW wallet and put on hold. Release to seller when you confirm receipt.",
      );
      const res = await axios.get(`aggregator/order-request/${resolvedOrderId}`, {
        params: { view: "admin" },
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setOrderRequestForPay(normalizeOrderRequestForId(res, resolvedOrderId));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Pay TC failed");
    } finally {
      setPayTcLoading(false);
    }
  }, [resolvedOrderId]);

  const handleAcceptMediaPI = useCallback(async () => {
    if (!resolvedOrderId || !id) return;
    setAcceptPiLoading(true);
    try {
      const token = getToken();
      const opts = {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      };
      const buyerWantsInsurance = Number(invoice?.InsuranceCost) > 0;
      await axios.post(
        `inventory/order-request/${resolvedOrderId}/accept-pi-choices`,
        { invoiceId: id, buyerTransportChoice: "Buyer", buyerWantsInsurance },
        opts,
      );
      toast.success(
        'PI accepted. Use "Pay TC" on the order or BXI Inventory to debit BW wallet and put TC on hold.',
      );
      const [invRes, ordRes] = await Promise.all([
        axios.get(`api/v1/admin/get-invoice/${id}`, opts),
        axios.get(`aggregator/order-request/${resolvedOrderId}`, {
          ...opts,
          params: { view: "admin" },
        }),
      ]);
      setInvoice(invRes.data ?? null);
      setOrderRequestForPay(normalizeOrderRequestForId(ordRes, resolvedOrderId));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to accept PI");
    } finally {
      setAcceptPiLoading(false);
    }
  }, [resolvedOrderId, id, invoice?.InsuranceCost]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error || "invoice not found."}</Typography>
        <Button sx={{ mt: 1 }} onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }


  const seller = invoice.SellerDetails || {};
  const buyer = invoice.BuyerDetails || {};
  const products = invoice.ProductData || [];
  const totals = invoice.PITotals || {};
  const isMedia = invoice.IsMedia === true;
  const buyerPendingAcceptance =
    invoice?.BuyerInvoiceAcceptanceStatus !== "Accepted";
  const showAdminChooseTransportButton =
    Boolean(resolvedOrderId) && buyerPendingAcceptance && !isMedia;
  const showAdminAcceptMediaPI =
    Boolean(resolvedOrderId) &&
    isMedia &&
    buyerPendingAcceptance;
  const acceptMediaPiDisabledByOrder =
    orderRequestForPay != null && orderRequestForPay.status !== "pi_issued";
  const piNumber = invoice.PINumber || invoice._id?.toString().slice(-8) || "—";
  const piDate = invoice.createdAt
    ? new Date(invoice.createdAt).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
  const buyerGstin = buyer?.Address?.GSTIN || buyer?.GSTIN;

  // ── Charges & totals (same logic as user-side PI) ──
  const aggCommission = Number(invoice.aggregatorCommission) || 0;
  const aggCommissionGST = Number(invoice.aggregatorCommissionGST) || 0;
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  // Platform fee applies only on Trade Credits base (product TC + additional TC).
  const tcBaseForCommission =
    (Number(totals.TotalPriceInBXI) || 0) +
    (Number(totals.TotalAdditionalCostInBXI ?? totals.TotalAdditionalCostWtihoutGSTInBXI) || 0);
  const baseForCommission =
    tcBaseForCommission ||
    Number(totals.TotalCoinsWithAdditionalCost) ||
    Number(totals.TotalPriceInBXI) ||
    0;
  const commissionPct =
    baseForCommission > 0 && aggCommission > 0
      ? ((aggCommission / baseForCommission) * 100).toFixed(1)
      : 12;
  const commissionTaxableValue =
    aggCommission > 0
      ? aggCommission
      : round2((baseForCommission * Number(commissionPct)) / 100);
  const commissionTaxAmount =
    aggCommissionGST > 0
      ? aggCommissionGST
      : round2((((baseForCommission * Number(commissionPct)) / 100) * (18 / 100)));
  const showCommissionRow =
    invoice?.invoiceRole !== "received_from_seller" &&
    (Number(commissionTaxableValue) > 0 || Number(commissionTaxAmount) > 0);
  const transportData =
    invoice.BuyerChoosedTransportation || invoice.Transportationdata || {};
  const transportFee =
    Number(transportData.transportationfee) ||
    Number(transportData.Transportationfee) ||
    0;
  // Show logistics only after buyer has accepted (chosen transportation type seller/self); same as user-side PI fix
  const buyerAcceptedPI = invoice?.BuyerInvoiceAcceptanceStatus === "Accepted";
  const isMediaBwAggregatorPi = isMediaBrandWorldAggregatorPI(invoice);
  const isIgstInvoice = invoiceEffectiveIsIgst(invoice);
  const tdsInrAmount = Number(invoice?.TDS ?? 0);

  const TDS = isMediaBwAggregatorPi
    ? 0.1
    : Number(invoice?.TDS_PERCENT ?? 0.1);


  const GetTimeline = (timeline, boughtDates) => {
    let result = [];
    if (timeline === "Month") {
      const months = Math.ceil(boughtDates.length / 30);
      for (let i = 0; i < months; i++) {
        const startIndex = i * 30;
        const endIndex = Math.min((i + 1) * 30, boughtDates.length);
        result.push({
          startDate: boughtDates[startIndex],
          endDate: boughtDates[endIndex - 1],
        });
      }
    } else if (timeline === "Week") {
      const weeks = Math.ceil(boughtDates.length / 7);
      for (let i = 0; i < weeks; i++) {
        const startIndex = i * 7;
        const endIndex = Math.min((i + 1) * 7, boughtDates.length);
        result.push({
          startDate: boughtDates[startIndex],
          endDate: boughtDates[endIndex - 1],
        });
      }
    } else if (timeline === "Day") {
      result = boughtDates.map((date) => ({
        startDate: date,
        endDate: date,
      }));
    } else if (timeline === "Years") {
      const years = Math.ceil(boughtDates.length / 365);
      for (let i = 0; i < years; i++) {
        const startIndex = i * 365;
        const endIndex = Math.min((i + 1) * 365, boughtDates.length);
        result.push({
          startDate: boughtDates[startIndex],
          endDate: boughtDates[endIndex - 1],
        });
      }
    }

    return result;
  };
  
  const bwCommCharge = buildMediaBrandWorldCommissionCharge(invoice, {
    name: "BXI Commission (INR)",
  });

  const useBwAggregatorCommissionLine =
  isMediaBwAggregatorPi &&
  (bwCommCharge.taxableValue > 0 || bwCommCharge.totalTaxAmount > 0);

  // ── Backend-computed PI math (single source of truth) ───────────────────────
  // displayCalculation (buildPIDisplayCalculation — now media-aware) is attached by
  // the admin get-invoice endpoint onto `invoice`. When present, charge lines +
  // totals are READ from it. The legacy render summed PITotals.TotalGSTAmount (the
  // COMBINED key, which double-counts additional-cost GST) + the BW commission; the
  // backend total avoids that. Legacy kept as a fallback for paths without the block.
  const dc = invoice?.displayCalculation || null;
  const backendChargeLines = Array.isArray(dc?.displayChargeLines)
    ? dc.displayChargeLines
    : null;
  const legacyTotalTax =
    (Number(invoice?.PITotals?.TotalGSTAmount) || 0) +
    (Number(bwCommCharge?.totalTaxAmount) || 0);
  const legacyInvoiceTotal =
    (Number(invoice?.PITotals?.TotalBasicValue) || 0) +
    (Number(bwCommCharge?.taxableValue) || 0) +
    legacyTotalTax;
  const dispTotalTax = dc ? Number(dc.totalTaxAmount) || 0 : legacyTotalTax;
  const dispInvoiceTotal = dc
    ? Number(dc.invoiceTotalAmount) || 0
    : legacyInvoiceTotal;
  const dispAmountBeforeTax = dc
    ? Number(dc.amountBeforeTax) || 0
    : (Number(invoice?.PITotals?.TotalBasicValue) || 0) +
      (Number(bwCommCharge?.taxableValue) || 0);

  const ChargesArray = backendChargeLines || (useBwAggregatorCommissionLine
    ? [bwCommCharge]
    : [
    ...(showCommissionRow
      ? [
          {
            name: "BXI Commission (INR)",
            gst: 18,
            commission: invoice?.PITotals?.PlatformFeePercentage,
            hsn: "996211",
            taxableValue: invoice?.PITotals?.TotalPlatformFee,
            totalTaxAmount: invoice?.PITotals?.TotalPlatformFeeGST,
          },
        ]
      : []),
  ]);

  let totatlTaxableAmount = 0;
  products.forEach((item) => {
    totatlTaxableAmount += isMedia
      ? (item.DiscountedPrice ?? 0) *
        (item.TimelineToBought ?? 0) *
        (item.ProductQuantity ?? 1) *
        (item.BoughtSeconds ?? 0)
      : (item.PricePerUnitWithoutGST ?? 0) * (item.ProductQuantity ?? 1);
  });
  let AdcostTotalTaxableAmount = 0;
  products.forEach((item) => {
    getMediaProductAdditionalCostRows(item, invoice).forEach((ad) => {
      AdcostTotalTaxableAmount += Number(ad?.TotalTaxableAmount) || 0;
    });
  });
  let ChargesTaxableValue = 0;
  let ChargesTaxableAmount = 0;
  ChargesArray.forEach((r) => {
    ChargesTaxableValue += Number(r.taxableValue) || 0;
    ChargesTaxableAmount += Number(r.totalTaxAmount) || 0;
  });
  const amountbeforeTax =
    totatlTaxableAmount + AdcostTotalTaxableAmount + ChargesTaxableValue;
  const totalTaxAmount =
    (Number(totals.TotalGstAmount) || 0) +
    (Number(totals.TotalAdCostGstAmount) || 0) +
    ChargesTaxableAmount;
  const invoiceTotalAmount = amountbeforeTax + totalTaxAmount;
  const TotalQuantity = products.reduce(
    (sum, p) => sum + (p.ProductQuantity ?? 0),
    0,
  );
  const isIGST = invoiceEffectiveIsIgst(invoice);

  let Totalcount = 0;

  return (
    <Box
      sx={{
        bgcolor: "transparent",
        width: "100%",
        height: "100%",
      }}
      elevation={0}
    >
      <Box
        sx={{
          width: "100%",
          bgcolor: "white",
          mx: "auto",
          borderRadius: "17px",
          pb: "40px",
        }}
        elevation={1}
      >
        <PIActionBar
          onBack={() => navigate(-1)}
          printRef={componentRef}
          printName={`ProformaInvoice_${invoice?.PINumber}`}
        />
        {resolvedOrderId && (
          <Paper
            elevation={0}
            sx={{
              width: "95%",
              mx: "auto",
              minHeight: "50px",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              px: 1,
              py: 0.5,
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              {showAdminChooseTransportButton && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() =>
                    navigate(
                      `/admindashboard/performainvoice/${id}/${resolvedOrderId}/choose-transport`,
                    )
                  }
                  sx={{ textTransform: "none" }}
                >
                  Choose Transportation
                </Button>
              )}
              {showAdminAcceptMediaPI && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => void handleAcceptMediaPI()}
                  disabled={
                    acceptPiLoading ||
                    orderRequestLoading ||
                    acceptMediaPiDisabledByOrder
                  }
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#000",
                    color: "#fff",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#1a1a1a",
                      boxShadow: "none",
                    },
                  }}
                >
                  {acceptPiLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Accept PI"
                  )}
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  navigate(`/admindashboard/orderrequest/${resolvedOrderId}`)
                }
                sx={{
                  textTransform: "none",
                  borderColor: "#2563eb",
                  color: "#2563eb",
                  "&:hover": {
                    borderColor: "#1d4ed8",
                    backgroundColor: "rgba(37, 99, 235, 0.04)",
                  },
                }}
              >
                Order Request
              </Button>
              {showProcurementPayTc && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={handlePayProcurementTC}
                  disabled={payTcLoading}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#000",
                    color: "#fff",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#1a1a1a",
                      boxShadow: "none",
                    },
                  }}
                >
                  {payTcLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Pay TC"
                  )}
                </Button>
              )}
            </Box>
          </Paper>
        )}
        <Box ref={componentRef}>
          <Box
            sx={{
              width: "95%",
              mx: "auto",
              borderLeft: "1px solid #cdcdcd",
              borderRight: "1px solid #cdcdcd",
              borderBottom: "1px solid #cdcdcd",
              borderTop: "1px solid #cdcdcd",
              px: "0px",
            }}
          >
            <PICompanyHeader
              seller={invoice?.SellerDetails}
              buyer={invoice?.BuyerDetails}
              website={invoice?.BuyerDetails?.website}
              title={"Media Proforma Invoice for Buyer"}
              titleBg="#2261A2"
              titleRadius="0px"
              textStyle={CommongTextStyle}
              websiteSx={WebsiteText}
              headerRef={downloadRef}
            />
            <PIInfoRow
              left={
                <Typography sx={CommongTextStyle}>
                  PI Number: {invoice?.PINumber}
                </Typography>
              }
              right={
                <Typography sx={CommongTextStyle}>
                  PI Date: {new Date(invoice?.createdAt)?.toLocaleDateString()}
                </Typography>
              }
            />
            <PISellerBuyerDetails
              seller={invoice?.SellerDetails}
              buyer={invoice?.BuyerDetails}
              sellerCuid={invoice?.Seller_CUID}
              buyerCuid={invoice?.Buyer_CUID}
              sellerStateCode={invoice?.SellerDetails?.StateCode}
              buyerStateCode={invoice?.BuyerDetails?.StateCode}
              sellerGstin={invoice?.SellerDetails?.GSTIN}
              buyerGstin={invoice?.BuyerDetails?.GSTIN}
              headerBg="#2261A2"
              baseTextStyle={CommongTextStyle}
              titleTextStyle={TextStyleTitle}
              valueTextStyle={TextStyleTwo}
              alignItems="center"
            />
            <TableContainer
              component={Paper}
              sx={{
                maxWidth: "100%",
                mx: "auto",
              }}
            >
              <Table sx={{ minWidth: "700px" }} aria-label="customized table">
                <TableHead>
                  <TableRow
                    sx={{
                      height: "42px",
                      bgcolor: "#2261A2",
                    }}
                  >
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{ borderRight: "1px solid #CDCDCD" }}
                    >
                      <Typography sx={TableTextStyle}> Sr. No.</Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{ borderRight: "1px solid #CDCDCD" }}
                    >
                      <Typography sx={TableTextStyle}>
                        Product / Service Description
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{ borderRight: "1px solid #CDCDCD" }}
                    >
                      <Typography sx={TableTextStyle}>
                        {" "}
                        HSN / SAC <br />
                        Code
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{ borderRight: "1px solid #CDCDCD" }}
                    >
                      <Typography sx={TableTextStyle}>
                        Ad
                        <br /> (In Seconds)
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{ borderRight: "1px solid #CDCDCD" }}
                    >
                      <Typography sx={TableTextStyle}>
                        {" "}
                        Timeline <br />( Day / Week <br />/ Months )
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{ borderRight: "1px solid #CDCDCD" }}
                    >
                      <Typography sx={TableTextStyle}>
                        Spots/Screens
                        <br />/Units
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{ borderRight: "1px solid #CDCDCD" }}
                    >
                      <Typography sx={TableTextStyle}>
                        {dataGridrows?.at(0)?.ProductTypeId === "Multiplex ADs"
                          ? "Avg Rate"
                          : "Rate"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{ borderRight: "1px solid #CDCDCD" }}
                    >
                      <Typography sx={TableTextStyle}>Taxable Value</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice?.ProductData?.map((row, idx) => {
                    const TotalSec = row?.TotalSec;
                    Totalcount += 1;
                    let TotalScreenPrice = 0;
                    row?.Screens?.map((response) => {
                      TotalScreenPrice += response?.DiscountedPrice;
                    });
                    return (
                      <React.Fragment key={idx}>
                        <TableRow
                          sx={{
                            height: "42px",
                            backgroundColor: "#F7F7F7",
                          }}
                        >
                          <TableCell
                            align="center"
                            colSpan={1}
                            rowSpan={1}
                            sx={{ borderRight: "1px solid #CDCDCD" }}
                          >
                            <Typography
                              sx={{ ...TableBottomtext, maxWidth: "80px" }}
                            >
                              {Totalcount}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="left"
                            colSpan={1}
                            rowSpan={1}
                            sx={{ borderRight: "1px solid #CDCDCD" }}
                          >
                            <Typography sx={ProductNameTextStyle}>
                              {row?.ProductName}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={1}
                            rowSpan={1}
                            sx={{ borderRight: "1px solid #CDCDCD" }}
                          >
                            <Typography
                              sx={{
                                fontSize: "12px",
                                minWidth: "auto",
                                maxWidth: "100px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: "center",
                                "@media print": { fontSize: "10px" },
                              }}
                            >
                              {resolveMediaPiHsn(row, orderRequestForPay)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={1}
                            rowSpan={1}
                            sx={{
                              borderRight: "1px solid #CDCDCD",
                              textAlign: "right",
                              px: 0,
                            }}
                          >
                            <Typography sx={TableBottomtext}>
                              {(() => {
                                const secs = resolveMediaPiBoughtSeconds(
                                  row,
                                  invoice,
                                );
                                return secs ? `${secs} seconds` : "";
                              })()}
                              &nbsp;
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={1}
                            rowSpan={1}
                            sx={{
                              borderRight: "1px solid #CDCDCD",
                              textAlign: "right",
                              px: 0,
                            }}
                          >
                            <Typography sx={TableBottomtext}>
                              {formatMediaTimelineLabel(row, {
                                isAggregatorPi: isMediaBwAggregatorPi,
                                column: "bought",
                              }) ||
                                formatMediaTimelineLabel(row, {
                                  isAggregatorPi: isMediaBwAggregatorPi,
                                  column: "duration",
                                })}
                              &nbsp;
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={1}
                            rowSpan={1}
                            sx={{
                              borderRight: "1px solid #CDCDCD",
                              textAlign: "right",
                              px: 0,
                            }}
                          >
                            <Typography sx={TableBottomtext}>
                              <CommaSeparator Price={row?.ProductQuantity} />
                              {row?.ProductQuantity ? " spots" : ""}
                              &nbsp;
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={1}
                            rowSpan={1}
                            sx={{
                              borderRight: "1px solid #CDCDCD",
                              textAlign: "right",
                              px: 0,
                            }}
                          >
                            <Typography sx={TableBottomtext}>
                              <CommaSeparator
                                Price={
                                  row?.Screens
                                    ? TotalScreenPrice / row?.Screens?.length
                                    : row?.DiscountedPrice
                                }
                              />
                              &nbsp;
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={1}
                            rowSpan={1}
                            sx={{
                              borderRight: "1px solid #CDCDCD",
                              textAlign: "right",
                              px: 0,
                            }}
                          >
                            <Typography sx={TableBottomtext}>
                              <CommaSeparator
                                Price={
                                  invoice?.IsMedia
                                    ? mediaBrandWorldProductLineTaxableExGst(
                                        row,
                                      )
                                    : row?.TotalPriceWithoutGST
                                }
                              />
                              &nbsp;
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Bought Dates */}
                        {!isMediaBwAggregatorPi &&
                          GetTimeline(row?.Timeline, row?.BoughtDates).map(
                            (el, idx) => {
                              return (
                                <TableRow
                                  key={idx}
                                  sx={{
                                    height: "25px",
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  <TableCell
                                    align="center"
                                    colSpan={1}
                                    rowSpan={1}
                                    sx={{
                                      borderRight: "1px solid #CDCDCD",
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        ...TableBottomtext,
                                        maxWidth: "80px",
                                      }}
                                    >
                                      &nbsp;
                                    </Typography>
                                  </TableCell>
                                  <TableCell
                                    align="left"
                                    colSpan={1}
                                    rowSpan={1}
                                    sx={{
                                      borderRight: "1px solid #CDCDCD",
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  >
                                    {row.Timeline === "Day" ? (
                                      <Typography
                                        sx={{
                                          fontSize: "12px",
                                          minWidth: "auto",
                                          maxWidth: "250px",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "flex-start",
                                          "@media print": { fontSize: "10px" },
                                        }}
                                      >
                                        &nbsp;
                                        {idx + 1}.{" "}
                                        {new Date(el.startDate).toDateString()}
                                      </Typography>
                                    ) : (
                                      <Typography
                                        sx={{
                                          fontSize: "12px",
                                          minWidth: "auto",
                                          maxWidth: "250px",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "flex-start",
                                          "@media print": { fontSize: "10px" },
                                        }}
                                      >
                                        &nbsp;
                                        {idx + 1}.{" "}
                                        {new Date(el.startDate).toDateString()}{" "}
                                        to {new Date(el.endDate).toDateString()}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    colSpan={1}
                                    rowSpan={1}
                                    sx={{
                                      borderRight: "1px solid #CDCDCD",
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        minWidth: "auto",
                                        maxWidth: "100px", // Prevents text from making the TableCell expand
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        textAlign: "center",
                                        "@media print": { fontSize: "10px" },
                                      }}
                                    ></Typography>
                                  </TableCell>

                                  <TableCell
                                    align="center"
                                    colSpan={1}
                                    rowSpan={1}
                                    sx={{
                                      borderRight: "1px solid #CDCDCD",
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        minWidth: "auto",
                                        maxWidth: "100px", // Prevents text from making the TableCell expand
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        textAlign: "center",
                                        "@media print": { fontSize: "10px" },
                                      }}
                                    ></Typography>
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    colSpan={1}
                                    rowSpan={1}
                                    sx={{
                                      borderRight: "1px solid #CDCDCD",
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        minWidth: "auto",
                                        maxWidth: "100px", // Prevents text from making the TableCell expand
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        textAlign: "center",
                                        "@media print": { fontSize: "10px" },
                                      }}
                                    ></Typography>
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    colSpan={1}
                                    rowSpan={1}
                                    sx={{
                                      borderRight: "1px solid #CDCDCD",
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        ...TableBottomtext,
                                        color: "rgba(5, 5, 5, 1)",
                                        opacity: 1,
                                      }}
                                    ></Typography>
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    colSpan={1}
                                    rowSpan={1}
                                    sx={{
                                      borderRight: "1px solid #CDCDCD",
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        ...TableBottomtext,
                                        color: "rgba(5, 5, 5, 1)",
                                        opacity: 1,
                                      }}
                                    ></Typography>
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    colSpan={1}
                                    rowSpan={1}
                                    sx={{
                                      borderRight: "1px solid #CDCDCD",
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        ...TableBottomtext,
                                        color: "rgba(5, 5, 5, 1)",
                                        opacity: 1,
                                      }}
                                    ></Typography>
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    colSpan={1}
                                    rowSpan={1}
                                    sx={{
                                      borderRight: "1px solid #CDCDCD",
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        ...TableBottomtext,
                                        color: "rgba(5, 5, 5, 1)",
                                        opacity: 1,
                                      }}
                                    ></Typography>
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    colSpan={1}
                                    rowSpan={1}
                                    sx={{
                                      borderRight: "1px solid #CDCDCD",
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        ...TableBottomtext,
                                        color: "rgba(5, 5, 5, 1)",
                                        opacity: 1,
                                      }}
                                    ></Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            },
                          )}
                      </React.Fragment>
                    );
                  })}

                  <TableRow
                    sx={{
                      height: "25px",
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography sx={{ ...TableBottomtext, maxWidth: "80px" }}>
                        &nbsp;
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="left"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "100px",
                          maxWidth: "200px", // Prevents text from making the TableCell expand
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          "@media print": { fontSize: "10px" },
                        }}
                      >
                        &nbsp;
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        fontSize: "12px",
                        minWidth: "auto",
                        maxWidth: "100px", // Prevents text from making the TableCell expand
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        "@media print": { fontSize: "10px" },
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>

                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "auto",
                          maxWidth: "100px", // Prevents text from making the TableCell expand
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          "@media print": { fontSize: "10px" },
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "auto",
                          maxWidth: "100px", // Prevents text from making the TableCell expand
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          "@media print": { fontSize: "10px" },
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>
                    {/* <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell> */}
                  </TableRow>

                  <TableRow
                    sx={{
                      height: "25px",
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography sx={{ ...TableBottomtext, maxWidth: "80px" }}>
                        &nbsp;
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="left"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "100px",
                          maxWidth: "220px", // Prevents text from making the TableCell expand
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          "@media print": { fontSize: "10px" },
                        }}
                      >
                        &nbsp;&nbsp;&nbsp;&nbsp;Additional Cost
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        fontSize: "12px",
                        minWidth: "auto",
                        maxWidth: "100px", // Prevents text from making the TableCell expand
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        "@media print": { fontSize: "10px" },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "auto",
                          maxWidth: "100px", // Prevents text from making the TableCell expand
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          "@media print": { fontSize: "10px" },
                        }}
                      ></Typography>
                    </TableCell>

                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "auto",
                          maxWidth: "100px", // Prevents text from making the TableCell expand
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          "@media print": { fontSize: "10px" },
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "auto",
                          maxWidth: "100px", // Prevents text from making the TableCell expand
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          "@media print": { fontSize: "10px" },
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "auto",
                          maxWidth: "100px", // Prevents text from making the TableCell expand
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          "@media print": { fontSize: "10px" },
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>
                    {/* <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell> */}
                    {/* <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell> */}
                  </TableRow>

                  {/* Additional Costs */}
                  {invoice?.ProductData?.map((row, idx) => {
                    const TotalSec = row?.TotalSec;
                    return (
                      <React.Fragment key={idx}>
                        {getMediaProductAdditionalCostRows(
                          row,
                          invoice,
                        ).map((res, index) => {
                            Totalcount += 1;
                            return (
                              <TableRow
                                sx={{
                                  height: "25px",
                                  padding: 0,
                                  margin: 0,
                                }}
                                key={index}
                              >
                                <TableCell
                                  align="center"
                                  colSpan={1}
                                  rowSpan={1}
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...TableBottomtext,
                                      maxWidth: "80px",
                                    }}
                                  >
                                    {Totalcount}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="left"
                                  colSpan={1}
                                  rowSpan={1}
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    padding: 0,
                                    margin: 0,
                                    // px: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      minWidth: "auto",
                                      maxWidth: "250px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-start",
                                      "@media print": { fontSize: "10px" },
                                    }}
                                  >
                                    {" "}
                                    &nbsp;{res?.ReasonOfCost} {"  "}({" "}
                                    {res?.AdCostApplicableOn === "PerUnit"
                                      ? "Per Unit"
                                      : "One Time"}{" "}
                                    ){" "}
                                    {res?.currencyType === "₹" ? (
                                      "₹"
                                    ) : (
                                      <img
                                        src={BxiCoin}
                                        style={{
                                          width: "20px",
                                          height: "auto",
                                        }}
                                        alt="BXI coins"
                                        title="BXI coins"
                                      />
                                    )}{" "}
                                    ({row?.ProductName}){" "}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  colSpan={1}
                                  rowSpan={1}
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      minWidth: "auto",
                                      maxWidth: "100px", // Prevents text from making the TableCell expand
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-start",
                                      "@media print": { fontSize: "10px" },
                                    }}
                                  >
                                    {formatHsnForDisplay(res?.AdCostHSN)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  colSpan={1}
                                  rowSpan={1}
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      minWidth: "auto",
                                      maxWidth: "100px", // Prevents text from making the TableCell expand
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      textAlign: "center",
                                      "@media print": { fontSize: "10px" },
                                    }}
                                  ></Typography>
                                </TableCell>{" "}
                                {/* <TableCell
                                  align="center"
                                  colSpan={1}
                                  rowSpan={1}
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      minWidth: "auto",
                                      maxWidth: "100px", // Prevents text from making the TableCell expand
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      textAlign: "center",
                                      "@media print": { fontSize: "10px" },
                                    }}
                                  ></Typography>
                                </TableCell>{" "} */}
                                {/* <TableCell
                                  align="center"
                                  colSpan={1}
                                  rowSpan={1}
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...TableBottomtext,
                                      color: "rgba(5, 5, 5, 1)",
                                      opacity: 1,
                                    }}
                                  ></Typography>
                                </TableCell> */}
                                <TableCell
                                  align="center"
                                  colSpan={1}
                                  rowSpan={1}
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...TableBottomtext,
                                      color: "rgba(5, 5, 5, 1)",
                                      opacity: 1,
                                      textAlign: "right",
                                    }}
                                  >
                                    {res?.AdCostApplicableOn === "PerUnit" ? (
                                      <>
                                        {row?.TotalSec && (
                                          <>
                                            {row?.ProductTypeId ===
                                              "MediaOffline" ||
                                            row?.ProductTypeId ===
                                              "News Papers / Magazines"
                                              ? `${row?.TimelineToBought}`
                                              : `${TotalSec}`}
                                          </>
                                        )}
                                      </>
                                    ) : null}
                                    &nbsp;
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  colSpan={1}
                                  rowSpan={1}
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...TableBottomtext,
                                      color: "rgba(5, 5, 5, 1)",
                                      opacity: 1,
                                      textAlign: "right",
                                    }}
                                  >
                                    {res?.AdCostApplicableOn === "PerUnit" ? (
                                      <CommaSeparator
                                        Price={row?.ProductQuantity}
                                      />
                                    ) : null}
                                    &nbsp;
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  colSpan={1}
                                  rowSpan={1}
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...TableBottomtext,
                                      color: "rgba(5, 5, 5, 1)",
                                      opacity: 1,
                                      textAlign: "right",
                                    }}
                                  >
                                    <CommaSeparator
                                      Price={res?.PriceWithoutGST}
                                    />
                                    &nbsp;
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  colSpan={1}
                                  rowSpan={1}
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...TableBottomtext,
                                      color: "rgba(5, 5, 5, 1)",
                                      opacity: 1,
                                      textAlign: "right",
                                    }}
                                  >
                                    <CommaSeparator
                                      Price={res?.TotalTaxableAmount}
                                    />
                                    &nbsp;
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          },
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* Charges Costs */}
                  {ChargesArray?.map((res, idx) => {
                    Totalcount += 1;
                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          height: "25px",
                          padding: 0,
                          margin: 0,
                        }}
                      >
                        <TableCell
                          align="center"
                          colSpan={1}
                          rowSpan={1}
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <Typography
                            sx={{ ...TableBottomtext, maxWidth: "80px" }}
                          >
                            {Totalcount}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="left"
                          colSpan={1}
                          rowSpan={1}
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              minWidth: "auto",
                              maxWidth: "250px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-start",
                              color: res?.name?.includes("BXI Commission")
                                ? "red"
                                : "#000000",
                              "@media print": { fontSize: "10px" },
                            }}
                          >
                            &nbsp;{res?.name}{" "}
                            {res?.name?.includes("BXI Commission") &&
                            res?.commission != null &&
                            Number(res?.commission) > 0
                              ? `@ ${res?.commission}%`
                              : ""}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          colSpan={1}
                          rowSpan={1}
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              minWidth: "auto",
                              maxWidth: "100px", // Prevents text from making the TableCell expand
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign: "center",
                              "@media print": { fontSize: "10px" },
                            }}
                          >
                            {formatHsnForDisplay(res?.hsn)}
                          </Typography>
                        </TableCell>

                        {/* <TableCell
                          align="center"
                          colSpan={1}
                          rowSpan={1}
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              minWidth: "auto",
                              maxWidth: "100px", // Prevents text from making the TableCell expand
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign: "center",
                              "@media print": { fontSize: "10px" },
                            }}
                          ></Typography>
                        </TableCell> */}
                        {/* <TableCell
                          align="center"
                          colSpan={1}
                          rowSpan={1}
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              minWidth: "auto",
                              maxWidth: "100px", // Prevents text from making the TableCell expand
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign: "center",
                              "@media print": { fontSize: "10px" },
                            }}
                          ></Typography>
                        </TableCell> */}
                        <TableCell
                          align="center"
                          colSpan={1}
                          rowSpan={1}
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              ...TableBottomtext,
                              color: "rgba(5, 5, 5, 1)",
                              opacity: 1,
                            }}
                          ></Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          colSpan={1}
                          rowSpan={1}
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              ...TableBottomtext,
                              color: "rgba(5, 5, 5, 1)",
                              opacity: 1,
                            }}
                          ></Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          colSpan={1}
                          rowSpan={1}
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              ...TableBottomtext,
                              color: "rgba(5, 5, 5, 1)",
                              opacity: 1,
                              textAlign: "right",
                            }}
                          >
                            &nbsp;
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          colSpan={1}
                          rowSpan={1}
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              ...TableBottomtext,
                              color: "rgba(5, 5, 5, 1)",
                              opacity: 1,
                              textAlign: "right",
                            }}
                          >
                            <CommaSeparator Price={res?.taxableValue} />
                            &nbsp;
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          colSpan={1}
                          rowSpan={1}
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              ...TableBottomtext,
                              color: "rgba(5, 5, 5, 1)",
                              opacity: 1,
                              textAlign: "right",
                            }}
                          >
                            <CommaSeparator Price={res?.taxableValue} />
                            &nbsp;
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  <TableRow
                    sx={{
                      height: "25px",
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography sx={{ ...TableBottomtext, maxWidth: "80px" }}>
                        &nbsp;
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="left"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "100px",
                          maxWidth: "150px", // Prevents text from making the TableCell expand
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          "@media print": { fontSize: "10px" },
                        }}
                      >
                        &nbsp;
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "auto",
                          maxWidth: "100px", // Prevents text from making the TableCell expand
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          "@media print": { fontSize: "10px" },
                        }}
                      ></Typography>
                    </TableCell>

                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "auto",
                          maxWidth: "100px", // Prevents text from making the TableCell expand
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          "@media print": { fontSize: "10px" },
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "auto",
                          maxWidth: "100px", // Prevents text from making the TableCell expand
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          "@media print": { fontSize: "10px" },
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>
                    {/* <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell> */}
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>
                  </TableRow>

                  {/* Total Costs */}
                  <TableRow
                    sx={{
                      height: "42px",
                      backgroundColor: "#F7F7F7",
                    }}
                  >
                    <TableCell
                      align="center"
                      colSpan={2}
                      rowSpan={3}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        background: "#2261A2",
                      }}
                    >
                      <Typography sx={{ ...TableBottomtext, color: "white" }}>
                        Total amount before Tax
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{ borderRight: "1px solid #CDCDCD" }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        textAlign: "right",
                        px: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "auto",
                          maxWidth: "100px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          "@media print": { fontSize: "10px" },
                        }}
                      ></Typography>
                    </TableCell>{" "}
                    {/* <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        textAlign: "right",
                        px: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "auto",
                          maxWidth: "100px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          "@media print": { fontSize: "10px" },
                        }}
                      ></Typography>
                    </TableCell>{" "} */}
                    {/* <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        textAlign: "right",
                        px: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          minWidth: "auto",
                          maxWidth: "100px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          "@media print": { fontSize: "10px" },
                        }}
                      ></Typography>
                    </TableCell>{" "} */}
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        textAlign: "right",
                        px: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      ></Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        textAlign: "right",
                        px: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      >
                        TotalQuantity
                        &nbsp;
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        textAlign: "right",
                        px: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      >
                        &nbsp;
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        borderRight: "1px solid #CDCDCD",
                        textAlign: "right",
                        px: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TableBottomtext,
                          color: "rgba(5, 5, 5, 1)",
                          opacity: 1,
                        }}
                      >
                        <CommaSeparator
                          Price={dispAmountBeforeTax.toFixed(2)}
                        />
                        &nbsp;
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>

                <TableBody>
                  <TableRow
                    sx={{
                      height: "21px",
                    }}
                  >
                    <TableCell
                      align="left"
                      colSpan={5}
                      rowSpan={1}
                      sx={{
                        ...tablecell,
                        borderTop: "none",
                        borderBottom: "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "14px",
                          fontWeight: 500,
                          fontStyle: "italic",
                        }}
                      >
                        Terms & Conditions
                      </Typography>
                    </TableCell>

                    <TableCell
                      align="left"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        p: 0,
                        maxWidth: "100px",
                        borderRight: "1px solid #CDCDCD",
                        mx: "auto",
                      }}
                    >
                      {" "}
                      <Typography sx={TableBottomtext}>
                        {" "}
                        &nbsp;&nbsp;Add: GST
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="left"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        p: 0,
                        maxWidth: "100px",
                        borderRight: "1px solid #CDCDCD",
                        mx: "auto",
                      }}
                    >
                      <Typography sx={TableBottomtext}>
                        &nbsp;&nbsp;CGST
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      colSpan={1}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      {!isIgstInvoice ? (
                        <Typography sx={TableBottomtext}>
                          <CommaSeparator Price={dispTotalTax / 2} />
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      height: "21px",
                    }}
                  >
                    <TableCell
                      align="left"
                      colSpan={5}
                      rowSpan={1}
                      sx={{
                        ...tablecell,
                        borderTop: "none",
                        borderBottom: "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                      >
                        Payment 100% in advance
                      </Typography>
                    </TableCell>

                    <TableCell
                      align="left"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        p: 0,
                        maxWidth: "100px",
                        borderRight: "1px solid #CDCDCD",
                        mx: "auto",
                      }}
                    ></TableCell>
                    <TableCell
                      align="left"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        p: 0,
                        maxWidth: "100px",
                        borderRight: "1px solid #CDCDCD",
                        mx: "auto",
                      }}
                    >
                      <Typography sx={TableBottomtext}>
                        &nbsp;&nbsp;SGST
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      colSpan={1}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      {!isIgstInvoice ? (
                        <Typography sx={TableBottomtext}>
                          <CommaSeparator Price={dispTotalTax / 2} />
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      height: "21px",
                    }}
                  >
                    <TableCell
                      align="left"
                      colSpan={5}
                      rowSpan={1}
                      sx={{
                        ...tablecell,
                        borderTop: "none",
                      }}
                    ></TableCell>

                    <TableCell
                      align="left"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        p: 0,
                        maxWidth: "100px",
                        borderRight: "1px solid #CDCDCD",
                        mx: "auto",
                      }}
                    ></TableCell>
                    <TableCell
                      align="left"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        p: 0,
                        maxWidth: "100px",
                        borderRight: "1px solid #CDCDCD",
                        mx: "auto",
                      }}
                    >
                      <Typography sx={TableBottomtext}>
                        &nbsp;&nbsp;IGST
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      colSpan={1}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      {isIgstInvoice ? (
                        <Typography sx={TableBottomtext}>
                          <CommaSeparator Price={dispTotalTax.toFixed(2)} />
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      height: "21px",
                    }}
                  >
                    <TableCell
                      align="left"
                      colSpan={5}
                      rowSpan={1}
                      sx={{
                        ...tablecell,
                        // borderTop: "none",
                        borderBottom: "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "14px",
                          fontWeight: 500,
                          fontStyle: "italic",
                        }}
                      >
                        Total Payable Amount (in words)
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="left"
                      colSpan={2}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography sx={TableBottomtext}>
                        Total GST Amount
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      colSpan={1}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography sx={TableBottomtext}>
                        <CommaSeparator Price={dispTotalTax.toFixed(2)} />
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      height: "21px",
                      borderTop: "none",
                      borderBottom: "none",
                    }}
                  >
                    <TableCell
                      align="left"
                      colSpan={5}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "12px",
                          fontWeight: 500,
                          fontStyle: "italic",
                        }}
                      >
                        {invoice?.PITotals ? (
                          <NumberToWord number={dispInvoiceTotal.toFixed(2)} />
                        ) : null}
                      </Typography>
                    </TableCell>
 
                    <TableCell
                      align="left"
                      colSpan={2}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography sx={TableBottomtext}>
                        invoice Total Amount
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      colSpan={1}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography sx={TableBottomtext}>
                        <CommaSeparator Price={dispInvoiceTotal.toFixed(2)} />
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      height: "21px",
                      borderTop: "none",
                      borderBottom: "none",
                    }}
                  >
                    <TableCell
                      align="left"
                      colSpan={5}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "12px",
                          fontWeight: 500,
                          fontStyle: "italic",
                          color: "#555",
                        }}
                      >
                        TDS under Section 194-O (amount withheld / settled per
                        order)
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="left"
                      colSpan={2}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography sx={TableBottomtext}>TDS (INR)</Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      colSpan={1}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography sx={TableBottomtext}>
                        <CommaSeparator Price={tdsInrAmount} />
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      height: "21px",
                      borderTop: "none",
                      borderBottom: "none",
                    }}
                  >
                    <TableCell
                      align="left"
                      colSpan={5}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#4361ee",
                        }}
                      >
                        * Our services are subject to TDS of {TDS}% u/s 393(1)8(v) of
                        Income Tax Act 2025 (section 194O of the old Income Tax Act 1961)
                      </Typography>
                    </TableCell>

                    <TableCell
                      align="left"
                      colSpan={2}
                      rowSpan={1}
                      sx={{
                        ...tablecell,
                        borderTop: "1px solid #CDCDCD",
                        borderLeft: "1px solid #CDCDCD",
                      }}
                    >
                      <Typography sx={TableBottomtext}>
                        Total Payable (Round off)
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      colSpan={1}
                      rowSpan={1}
                      sx={{
                        ...tablecell,
                        borderTop: "1px solid #CDCDCD",
                      }}
                    >
                      <Typography sx={TableBottomtext}>
                        <CommaSeparator Price={invoice?.PITotals?.GrandTotal?.toFixed(2)} />
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow
                    sx={{
                      height: "21px",
                      borderTop: "none",
                      borderBottom: "none",
                    }}
                  >
                    <TableCell
                      align="left"
                      colSpan={7}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#4361ee",
                        }}
                      ></Typography>
                    </TableCell>

                    <TableCell
                      align="left"
                      colSpan={2}
                      rowSpan={1}
                      sx={{
                        ...tablecell,
                        borderLeft: "1px solid #CDCDCD",
                      }}
                    >
                      <Typography sx={TableBottomtext}>
                        {/* Total Payable (Round off) */}
                      </Typography>
                    </TableCell>

                    <TableCell
                      align="right"
                      colSpan={1}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography sx={TableBottomtext}></Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      height: "21px",
                      borderTop: "none",
                      borderBottom: "2px solid #CDCDCD",
                    }}
                  >
                    <TableCell
                      align="left"
                      colSpan={7}
                      rowSpan={1}
                      sx={tablecell}
                    ></TableCell>

                    <TableCell
                      align="left"
                      colSpan={2}
                      rowSpan={1}
                      sx={{
                        ...tablecell,
                        borderLeft: "2px solid #CDCDCD",
                      }}
                    >
                      <Typography sx={TableBottomtext}></Typography>
                    </TableCell>

                    <TableCell
                      align="right"
                      colSpan={1}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography sx={TableBottomtext}></Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Table
                sx={{
                  borderLeft: "1px solid #CDCDCD",
                }}
              >
                <TableBody>
                  <TableRow
                    sx={{
                      height: "21px",
                      borderBottom: "2px solid #CDCDCD",
                    }}
                  >
                    <TableCell
                      sx={{
                        p: 0,
                        maxWidth: "180px",
                        borderRight: "1px solid #CDCDCD",
                      }}
                      align="center"
                    >
                      <TableRow
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{
                            width: "180px",
                            p: 0,
                            // bgcolor: "green",
                            height: "20px",
                            border: "none",
                          }}
                        >
                          <Typography
                            sx={{
                              ...TableBottomtext,
                              fontWeight: 600,
                            }}
                          >
                            HSN / SAC
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0,
                        maxWidth: "180px",
                        mx: "auto",
                        borderRight: "1px solid #CDCDCD",
                      }}
                      align="center"
                    >
                      <TableRow
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{
                            width: "180px",
                            p: 0,
                            height: "20px",
                            border: "none",
                          }}
                        >
                          <Typography
                            sx={{
                              ...TableBottomtext,
                              fontWeight: 600,
                            }}
                          >
                            Taxable Value
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0,
                        maxWidth: "220px",
                        mx: "auto",
                      }}
                      align="center"
                    >
                      <TableRow>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{
                            width: "220px",
                            borderRight: "1px solid #CDCDCD",
                            p: 0,
                            height: "20px",
                          }}
                        >
                          <Typography
                            sx={{
                              ...TableBottomtext,
                              fontWeight: 600,
                            }}
                          >
                            CGST Tax
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          align="center"
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            borderBottom: "none",
                            p: 0,
                            height: "20px",
                            width: "25%",
                          }}
                        >
                          {" "}
                          <Typography
                            sx={{
                              ...TableBottomtext,
                            }}
                          >
                            Rate
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: "25%",
                            borderRight: "1px solid #CDCDCD",
                            borderBottom: "none",
                            p: 0,
                            height: "20px",
                          }}
                        >
                          {" "}
                          <Typography
                            sx={{
                              ...TableBottomtext,
                            }}
                          >
                            Amount
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0,
                        maxWidth: "220px",
                        mx: "auto",
                      }}
                      align="center"
                    >
                      <TableRow>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{
                            width: "220px",
                            borderRight: "1px solid #CDCDCD",
                            p: 0,
                            height: "20px",
                          }}
                        >
                          <Typography
                            sx={{
                              ...TableBottomtext,
                              fontWeight: 600,
                            }}
                          >
                            SGST Tax
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          align="center"
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            borderBottom: "none",
                            p: 0,
                            height: "20px",
                            width: "25%",
                          }}
                        >
                          {" "}
                          <Typography
                            sx={{
                              ...TableBottomtext,
                            }}
                          >
                            Rate
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: "25%",
                            borderRight: "1px solid #CDCDCD",
                            borderBottom: "none",
                            p: 0,
                            height: "20px",
                          }}
                        >
                          {" "}
                          <Typography
                            sx={{
                              ...TableBottomtext,
                            }}
                          >
                            Amount
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0,
                        maxWidth: "220px",
                        mx: "auto",
                      }}
                      align="center"
                    >
                      <TableRow>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{
                            width: "220px",
                            borderRight: "1px solid #CDCDCD",
                            p: 0,
                            height: "20px",
                          }}
                        >
                          <Typography
                            sx={{
                              ...TableBottomtext,
                              fontWeight: 600,
                            }}
                          >
                            Integrated Tax
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          align="center"
                          sx={{
                            borderRight: "1px solid #CDCDCD",
                            borderBottom: "none",
                            p: 0,
                            height: "20px",
                            width: "25%",
                          }}
                        >
                          {" "}
                          <Typography
                            sx={{
                              ...TableBottomtext,
                            }}
                          >
                            Rate
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: "25%",
                            borderRight: "1px solid #CDCDCD",
                            borderBottom: "none",
                            p: 0,
                            height: "20px",
                          }}
                        >
                          {" "}
                          <Typography
                            sx={{
                              ...TableBottomtext,
                            }}
                          >
                            Amount
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0,
                        maxWidth: "200px",
                        mx: "auto",
                        borderRight: "1px solid #CDCDCD",
                      }}
                      align="center"
                    >
                      <TableRow
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{
                            width: "200px",
                            border: "none",
                            p: 0,
                            height: "20px",
                          }}
                        >
                          <Typography
                            sx={{
                              ...TableBottomtext,
                              fontWeight: 600,
                            }}
                          >
                            Total Tax Amount
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableCell>
                  </TableRow>

                  {invoice?.ProductData?.map((res, idx) => {
                    const summaryRowTaxable = invoice?.IsMedia
                      ? mediaBrandWorldProductLineTaxableExGst(res)
                      : Number(res?.TotalPriceWithoutGST) || 0;
                    const summaryRowGstAmt = invoice?.IsMedia
                      ? (summaryRowTaxable * (Number(res?.GST) || 0)) / 100
                      : Number(res?.TotalGSTAmount) || 0;
                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          height: "21px",
                        }}
                      >
                        <TableCell
                          sx={{
                            p: 0,
                            maxWidth: "180px",
                            borderRight: "1px solid #CDCDCD",
                          }}
                          align="center"
                        >
                          <TableRow
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <TableCell
                              align="center"
                              colSpan={2}
                              sx={{
                                width: "180px",
                                p: 0,
                                height: "20px",
                                border: "none",
                              }}
                            >
                              <Typography sx={TableBottomtext}>
                                {resolveMediaPiHsn(res, orderRequestForPay)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0,
                            maxWidth: "180px",
                            mx: "auto",
                            borderRight: "1px solid #CDCDCD",
                          }}
                        >
                          <TableRow
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "right",
                              alignContent: "center",
                              alignItems: "center",
                              px: 0,
                            }}
                          >
                            <TableCell
                              colSpan={2}
                              sx={{
                                width: "180px",
                                p: 0,
                                height: "20px",
                                border: "none",
                                textAlign: "right",
                              }}
                            >
                              <Typography sx={TableBottomtext}>
                                <CommaSeparator Price={summaryRowTaxable} />
                                &nbsp;
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0,
                            width: "220px",
                            mx: "auto",
                          }}
                          align="center"
                        >
                          <TableRow
                            sx={{
                              width: "220px",
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{
                                borderRight: "1px solid #CDCDCD",
                                p: 0,
                                height: "20px",
                                width: "110px",
                              }}
                            >
                              {" "}
                              {!isIgstInvoice ? (
                                <Typography sx={TableBottomtext}>
                                  <CommaSeparator Price={res?.GST / 2} />%
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                width: "110px",
                                borderRight: "1px solid #CDCDCD",
                                p: 0,
                                height: "20px",
                                textAlign: "right",
                                px: 0,
                              }}
                            >
                              {" "}
                              {!isIgstInvoice ? (
                                <Typography sx={TableBottomtext}>
                                  <CommaSeparator
                                    Price={summaryRowGstAmt / 2}
                                  />
                                  &nbsp;
                                </Typography>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0,
                            width: "220px",
                            mx: "auto",
                          }}
                          align="center"
                        >
                          <TableRow
                            sx={{
                              width: "220px",
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{
                                borderRight: "1px solid #CDCDCD",
                                p: 0,
                                height: "20px",
                                width: "110px",
                              }}
                            >
                              {" "}
                              {!isIgstInvoice ? (
                                <Typography sx={TableBottomtext}>
                                  <CommaSeparator Price={res?.GST / 2} />%
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                width: "110px",
                                borderRight: "1px solid #CDCDCD",
                                p: 0,
                                height: "20px",
                                textAlign: "right",
                                px: 0,
                              }}
                            >
                              {" "}
                              {!isIgstInvoice ? (
                                <Typography sx={TableBottomtext}>
                                  <CommaSeparator
                                    Price={summaryRowGstAmt / 2}
                                  />
                                  &nbsp;
                                </Typography>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0,
                            width: "220px",
                            mx: "auto",
                          }}
                          align="center"
                        >
                          <TableRow
                            sx={{
                              width: "220px",
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{
                                borderRight: "1px solid #CDCDCD",
                                p: 0,
                                height: "20px",
                                width: "110px",
                              }}
                            >
                              {" "}
                              {isIgstInvoice ? (
                                <Typography sx={TableBottomtext}>
                                  <CommaSeparator Price={res?.GST} /> %
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                width: "110px",
                                borderRight: "1px solid #CDCDCD",
                                p: 0,
                                height: "20px",
                                textAlign: "right",
                                px: 0,
                              }}
                            >
                              {" "}
                              {isIgstInvoice ? (
                                <Typography sx={TableBottomtext}>
                                  <CommaSeparator Price={summaryRowGstAmt} />
                                  &nbsp;
                                </Typography>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        </TableCell>

                        <TableCell
                          sx={{
                            p: 0,
                            maxWidth: "200px",
                            mx: "auto",
                            borderRight: "1px solid #CDCDCD",
                          }}
                          align="center"
                        >
                          <TableRow
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "right",
                              alignContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <TableCell
                              align="center"
                              colSpan={2}
                              sx={{
                                width: "200px",
                                border: "none",
                                p: 0,
                                height: "20px",
                                textAlign: "right",
                                "@media print": {
                                  maxWidth: "150px",
                                  textAlign: "left",
                                },
                              }}
                            >
                              <Typography sx={TableBottomtext}>
                                <CommaSeparator Price={summaryRowGstAmt} />
                                &nbsp;
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {invoice?.ProductData?.map((row, idx) => {
                    return getMediaProductAdditionalCostRows(
                      row,
                      invoice,
                    ).map((res, index) => {
                        const GstOfAdCost = Number(res?.AdCostGST);
                        return (
                          <TableRow
                            key={index}
                            sx={{
                              height: "21px",
                            }}
                          >
                            <TableCell
                              sx={{
                                p: 0,
                                maxWidth: "180px",
                                mx: "auto",
                                borderRight: "1px solid #CDCDCD",
                              }}
                              align="center"
                            >
                              <TableRow
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <TableCell
                                  align="center"
                                  colSpan={2}
                                  sx={{
                                    width: "180px",
                                    p: 0,
                                    height: "20px",
                                    border: "none",
                                  }}
                                >
                                  <Typography sx={TableBottomtext}>
                                    {formatHsnForDisplay(res?.AdCostHSN)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableCell>
                            <TableCell
                              sx={{
                                p: 0,
                                maxWidth: "180px",
                                mx: "auto",
                                borderRight: "1px solid #CDCDCD",
                              }}
                              align="center"
                            >
                              <TableRow
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "right",
                                  alignContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <TableCell
                                  align="center"
                                  colSpan={2}
                                  sx={{
                                    width: "180px",
                                    p: 0,
                                    height: "20px",
                                    border: "none",
                                    textAlign: "right",
                                    "@media print": {
                                      maxWidth: "120px",
                                      textAlign: "left",
                                    },
                                  }}
                                >
                                  <Typography sx={TableBottomtext}>
                                    <CommaSeparator
                                      Price={res?.TotalTaxableAmount}
                                    />
                                    &nbsp;
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableCell>
                            <TableCell
                              sx={{
                                p: 0,
                                width: "220px",
                                mx: "auto",
                              }}
                              align="center"
                            >
                              <TableRow
                                sx={{
                                  width: "220px",
                                }}
                              >
                                <TableCell
                                  align="center"
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    p: 0,
                                    height: "20px",
                                    width: "110px",
                                  }}
                                >
                                  {" "}
                                  {!isIgstInvoice ? (
                                    <Typography sx={TableBottomtext}>
                                      <CommaSeparator
                                        Price={res?.AdCostGST / 2}
                                      />
                                      %
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    width: "110px",
                                    borderRight: "1px solid #CDCDCD",
                                    p: 0,
                                    height: "20px",
                                    textAlign: "right",
                                    px: 0,
                                  }}
                                >
                                  {" "}
                                  {!isIgstInvoice ? (
                                    <Typography sx={TableBottomtext}>
                                      {res?.AdCostApplicableOn === "PerUnit" ? (
                                        <CommaSeparator
                                          Price={
                                            (res?.TotalTaxableAmount *
                                              (GstOfAdCost / 100)) /
                                            2
                                          }
                                        />
                                      ) : (
                                        <CommaSeparator
                                          Price={
                                            (res?.TotalTaxableAmount *
                                              (GstOfAdCost / 100)) /
                                            2
                                          }
                                        />
                                      )}
                                      &nbsp;
                                    </Typography>
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            </TableCell>
                            <TableCell
                              sx={{
                                p: 0,
                                width: "220px",
                                mx: "auto",
                              }}
                              align="center"
                            >
                              <TableRow
                                sx={{
                                  width: "220px",
                                }}
                              >
                                <TableCell
                                  align="center"
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    p: 0,
                                    height: "20px",
                                    width: "110px",
                                  }}
                                >
                                  {" "}
                                  {!isIgstInvoice ? (
                                    <Typography sx={TableBottomtext}>
                                      <CommaSeparator
                                        Price={res?.AdCostGST / 2}
                                      />
                                      %
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    width: "110px",
                                    borderRight: "1px solid #CDCDCD",
                                    p: 0,
                                    height: "20px",
                                    textAlign: "right",
                                    px: 0,
                                    "@media print": {
                                      maxWidth: "150px",
                                      textAlign: "left",
                                    },
                                  }}
                                >
                                  {" "}
                                  {!isIgstInvoice ? (
                                    <Typography sx={TableBottomtext}>
                                      {res?.AdCostApplicableOn === "PerUnit" ? (
                                        <CommaSeparator
                                          Price={
                                            (res?.TotalTaxableAmount *
                                              (GstOfAdCost / 100)) /
                                            2
                                          }
                                        />
                                      ) : (
                                        <CommaSeparator
                                          Price={
                                            (res?.TotalTaxableAmount *
                                              (GstOfAdCost / 100)) /
                                            2
                                          }
                                        />
                                      )}
                                      &nbsp;
                                    </Typography>
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            </TableCell>
                            <TableCell
                              sx={{
                                p: 0,
                                width: "220px",
                                mx: "auto",
                              }}
                              align="center"
                            >
                              <TableRow
                                sx={{
                                  width: "220px",
                                }}
                              >
                                <TableCell
                                  align="center"
                                  sx={{
                                    borderRight: "1px solid #CDCDCD",
                                    p: 0,
                                    height: "20px",
                                    width: "110px",
                                  }}
                                >
                                  {" "}
                                  {isIgstInvoice ? (
                                    <Typography sx={TableBottomtext}>
                                      <CommaSeparator Price={res?.AdCostGST} />{" "}
                                      %
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    width: "110px",
                                    borderRight: "1px solid #CDCDCD",
                                    p: 0,
                                    height: "20px",
                                    textAlign: "right",
                                    px: 0,
                                    "@media print": {
                                      maxWidth: "150px",
                                      textAlign: "left",
                                    },
                                  }}
                                >
                                  {" "}
                                  {isIgstInvoice ? (
                                    <Typography sx={TableBottomtext}>
                                      {res?.AdCostApplicableOn === "PerUnit" ? (
                                        <CommaSeparator
                                          Price={
                                            res?.TotalTaxableAmount *
                                            (GstOfAdCost / 100)
                                          }
                                        />
                                      ) : (
                                        <CommaSeparator
                                          Price={
                                            res?.TotalTaxableAmount *
                                            (GstOfAdCost / 100)
                                          }
                                        />
                                      )}
                                      &nbsp;
                                    </Typography>
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            </TableCell>

                            <TableCell
                              sx={{
                                p: 0,
                                maxWidth: "200px",
                                mx: "auto",
                                borderRight: "1px solid #CDCDCD",
                                "@media print": {
                                  maxWidth: "150px",
                                  textAlign: "left",
                                },
                              }}
                              align="center"
                            >
                              <TableRow
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "right",
                                  alignContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <TableCell
                                  align="center"
                                  colSpan={2}
                                  sx={{
                                    width: "200px",
                                    border: "none",
                                    p: 0,
                                    height: "20px",
                                    textAlign: "right",
                                    "@media print": {
                                      maxWidth: "150px",
                                      textAlign: "left",
                                    },
                                  }}
                                >
                                  <Typography sx={TableBottomtext}>
                                    {res?.AdCostApplicableOn === "PerUnit" ? (
                                      <CommaSeparator
                                        Price={
                                          res?.TotalTaxableAmount *
                                          (GstOfAdCost / 100)
                                        }
                                      />
                                    ) : (
                                      <CommaSeparator
                                        Price={
                                          res?.TotalTaxableAmount *
                                          (GstOfAdCost / 100)
                                        }
                                      />
                                    )}
                                    &nbsp;
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableCell>
                          </TableRow>
                        );
                      },
                    );
                  })}

                  {ChargesArray?.map((res, idx) => {
                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          height: "21px",
                        }}
                      >
                        <TableCell
                          sx={{
                            p: 0,
                            maxWidth: "180px",
                            mx: "auto",
                            borderRight: "1px solid #CDCDCD",
                          }}
                          align="center"
                        >
                          <TableRow
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <TableCell
                              align="center"
                              colSpan={2}
                              sx={{
                                width: "180px",
                                p: 0,
                                height: "20px",
                                border: "none",
                              }}
                            >
                              <Typography sx={TableBottomtext}>
                                {formatHsnForDisplay(res?.hsn)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0,
                            maxWidth: "180px",
                            mx: "auto",
                            borderRight: "1px solid #CDCDCD",
                          }}
                          align="center"
                        >
                          <TableRow
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "right",
                              alignContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <TableCell
                              align="center"
                              colSpan={2}
                              sx={{
                                width: "180px",
                                p: 0,
                                height: "20px",
                                border: "none",
                                textAlign: "right",
                              }}
                            >
                              <Typography sx={TableBottomtext}>
                                <CommaSeparator Price={res?.taxableValue} />
                                &nbsp;
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0,
                            width: "220px",
                            mx: "auto",
                          }}
                          align="center"
                        >
                          <TableRow
                            sx={{
                              width: "220px",
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{
                                borderRight: "1px solid #CDCDCD",
                                p: 0,
                                height: "20px",
                                width: "110px",
                              }}
                            >
                              {" "}
                              {!isIgstInvoice ? (
                                <Typography sx={TableBottomtext}>
                                  <CommaSeparator Price={res?.gst / 2} />%
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                width: "110px",
                                borderRight: "1px solid #CDCDCD",
                                p: 0,
                                height: "20px",
                                textAlign: "right",
                                px: 0,
                              }}
                            >
                              {" "}
                              {!isIgstInvoice ? (
                                <Typography sx={TableBottomtext}>
                                  <CommaSeparator
                                    Price={res?.totalTaxAmount / 2}
                                  />
                                  &nbsp;
                                </Typography>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0,
                            width: "220px",
                            mx: "auto",
                          }}
                          align="center"
                        >
                          <TableRow
                            sx={{
                              width: "220px",
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{
                                borderRight: "1px solid #CDCDCD",
                                p: 0,
                                height: "20px",
                                width: "110px",
                              }}
                            >
                              {" "}
                              {!isIgstInvoice ? (
                                <Typography sx={TableBottomtext}>
                                  <CommaSeparator Price={res?.gst / 2} />%
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                width: "110px",
                                borderRight: "1px solid #CDCDCD",
                                p: 0,
                                height: "20px",
                                textAlign: "right",
                                px: 0,
                              }}
                            >
                              {" "}
                              {!isIgstInvoice ? (
                                <Typography sx={TableBottomtext}>
                                  <CommaSeparator
                                    Price={res?.totalTaxAmount / 2}
                                  />
                                  &nbsp;
                                </Typography>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0,
                            width: "220px",
                            mx: "auto",
                          }}
                          align="center"
                        >
                          <TableRow
                            sx={{
                              width: "220px",
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{
                                borderRight: "1px solid #CDCDCD",
                                p: 0,
                                height: "20px",
                                width: "110px",
                              }}
                            >
                              {" "}
                              {isIgstInvoice ? (
                                <Typography sx={TableBottomtext}>
                                  <CommaSeparator Price={res?.gst} /> %
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                width: "110px",
                                borderRight: "1px solid #CDCDCD",
                                p: 0,
                                height: "20px",
                                textAlign: "right",
                                px: 0,
                              }}
                            >
                              {" "}
                              {isIgstInvoice ? (
                                <Typography sx={TableBottomtext}>
                                  <CommaSeparator Price={res?.totalTaxAmount} />
                                  &nbsp;
                                </Typography>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        </TableCell>

                        <TableCell
                          sx={{
                            p: 0,
                            maxWidth: "200px",
                            mx: "auto",
                            borderRight: "1px solid #CDCDCD",
                          }}
                          align="center"
                        >
                          <TableRow
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "right",
                              alignContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <TableCell
                              align="center"
                              colSpan={2}
                              sx={{
                                width: "200px",
                                border: "none",
                                p: 0,
                                height: "20px",
                                textAlign: "right",
                                px: 0,
                              }}
                            >
                              <Typography sx={TableBottomtext}>
                                <CommaSeparator Price={res?.totalTaxAmount} />
                                &nbsp;
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow
                    sx={{
                      height: "21px",
                    }}
                  >
                    <TableCell
                      sx={{
                        p: 0,
                        maxWidth: "180px",
                        mx: "auto",
                        borderRight: "1px solid #CDCDCD",
                      }}
                      align="center"
                    >
                      <TableRow
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{
                            width: "180px",
                            p: 0,
                            height: "20px",
                            border: "none",
                          }}
                        >
                          <Typography sx={TableBottomtext}></Typography>
                        </TableCell>
                      </TableRow>
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0,
                        maxWidth: "180px",
                        mx: "auto",
                        borderRight: "1px solid #CDCDCD",
                      }}
                      align="center"
                    >
                      <TableRow
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "right",
                          alignContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{
                            width: "180px",
                            p: 0,
                            height: "20px",
                            border: "none",
                            textAlign: "right",
                          }}
                        >
                          <Typography sx={TableBottomtext}>&nbsp;</Typography>
                        </TableCell>
                      </TableRow>
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0,
                        width: "220px",
                        mx: "auto",
                        borderRight: "1px solid #CDCDCD",
                      }}
                      align="center"
                    >
                      <TableRow
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "right",
                          alignContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{
                            width: "180px",
                            p: 0,
                            height: "20px",
                            border: "none",
                            textAlign: "right",
                          }}
                        >
                          <Typography sx={TableBottomtext}>&nbsp;</Typography>
                        </TableCell>
                      </TableRow>
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0,
                        width: "220px",
                        mx: "auto",
                        borderRight: "1px solid #CDCDCD",
                      }}
                      align="center"
                    >
                      <TableRow
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "right",
                          alignContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{
                            width: "180px",
                            p: 0,
                            height: "20px",
                            border: "none",
                            textAlign: "right",
                          }}
                        >
                          <Typography sx={TableBottomtext}>&nbsp;</Typography>
                        </TableCell>
                      </TableRow>
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0,
                        width: "220px",
                        mx: "auto",
                        borderRight: "1px solid #CDCDCD",
                      }}
                      align="center"
                    >
                      <TableRow
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "right",
                          alignContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{
                            width: "180px",
                            p: 0,
                            height: "20px",
                            border: "none",
                            textAlign: "right",
                          }}
                        >
                          <Typography
                            sx={{ ...TableBottomtext, fontWeight: 600 }}
                          >
                            Total GST Amount &nbsp;
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableCell>

                    <TableCell
                      sx={{
                        p: 0,
                        maxWidth: "200px",
                        mx: "auto",
                        borderRight: "1px solid #CDCDCD",
                      }}
                      align="center"
                    >
                      <TableRow
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "right",
                          alignContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{
                            width: "200px",
                            border: "none",
                            p: 0,
                            height: "20px",
                            textAlign: "right",
                            px: 0,
                          }}
                        >
                          <Typography
                            sx={{ ...TableBottomtext, fontWeight: 600 }}
                          >
                            <CommaSeparator Price={dispTotalTax.toFixed(2)} />
                            &nbsp;
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableCell>
                  </TableRow>
                </TableBody>

                <TableBody>
                  <TableRow>
                    <TableCell
                      align="center"
                      colSpan={7}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      &nbsp;
                    </TableCell>
                  </TableRow>
                </TableBody>
                <TableBody>
                  <TableRow>
                    <TableCell
                      align="center"
                      colSpan={7}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography sx={TableBottomtext}>
                        Certified that the particulars given above are true and
                        correct
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
                <TableBody>
                  <TableRow>
                    <TableCell
                      align="center"
                      colSpan={7}
                      rowSpan={1}
                      sx={tablecell}
                    >
                      <Typography sx={TableBottomtext}>
                        This is a System Generated invoice by Brand World
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          {dataGridrows?.length > 0
            ? (() => {
                const { cols, title } = pickMediaPiCols(
                  invoice?.ProductData?.at(0),
                  dataGridrows,
                );
                return (
                  <Box
                    sx={{
                      bgcolor: "#fff",
                      width: "100%",
                      justifyContent: "space-around",
                      alignContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: "#fff",
                        padding: "10px",
                        border: "1px solid #2261a2",
                        width: "94%",
                        mt: "10px",
                        mx: "auto",
                      }}
                    >
                      <Typography sx={{ ...TableHeadText, mb: 1 }}>
                        {title}
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Typography sx={TableHeadText}>
                                  Sr No.
                                </Typography>
                              </TableCell>
                              {cols.map((c) => (
                                <TableCell key={c.label}>
                                  <Typography sx={TableHeadText}>
                                    {c.label}
                                  </Typography>
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dataGridrows.map((resData, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <Typography sx={TableBodytext}>
                                    {resData?.srNo ?? index + 1}
                                  </Typography>
                                </TableCell>
                                {cols.map((c) => (
                                  <TableCell key={c.label}>
                                    <Typography sx={TableBodytext}>
                                      {c.pick(resData)}
                                    </Typography>
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Box>
                );
              })()
            : (
              <></>
            )}
        </Box>
      </Box>
    </Box>
  );
}

const TextStyleTwo = {
  fontFamily: "Poppins",
  fontStyle: "normal",
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "20px",
  textAlign: "left",
  opacity: 1,
  color: "#000000",
  "@media print": { fontStyle: "10px", lineHeight: "15px" },
};

const TextStyleTitle = {
  fontFamily: "Poppins",
  fontStyle: "normal",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  textAlign: "left",
  opacity: 1,
  color: "#000000",
  width: "120px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "@media print": { fontStyle: "10px", lineHeight: "17px", fontWeight: 400 },
};

const AddressTextStyleTitle = {
  fontFamily: "Poppins",
  fontStyle: "normal",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  textAlign: "left",
  color: "#505050",
  opacity: 1,

  width: "300px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "@media print": { fontStyle: "11px", lineHeight: "15px" },
};

const TableTextStyle = {
  fontFamily: "Poppins",
  fontStyle: "normal",
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "20px",
  color: "#FFFFFF",
  "@media print": { fontStyle: "10px", lineHeight: "15px" },
};

const TableBottomtext = {
  fontFamily: "Poppins",
  fontStyle: "normal",
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "20px",
  color: "#000000",
  opacity: 1,
  "@media print": { fontStyle: "11px", lineHeight: "15px" },
};
const tablecell = { borderRight: "1px solid #CDCDCD", p: 0.2 };
const TableHeadText = {
  fontFamily: "Poppins",
  fontSize: "14px",
  fontWeight: 500,
  fontStyle: "normal",
  color: "#2261a2",
};
const TableBodytext = {
  fontFamily: "Poppins",
  fontSize: "12px",
  fontWeight: 500,
  fontStyle: "normal",
};

const WebsiteText = {
  color: "#445FD2",
  fontSize: "14px",
  fontWeight: 600,
  fontFamily: "Poppins",
  fontStyle: "normal",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  height: "130px",
  width: "30%",
  borderLeft: "1px solid #cdcdcd",
};

const ProductNameTextStyle = {
  fontSize: "12px",
  minWidth: "auto",
  maxWidth: "250px", // Prevents text from making the TableCell expand
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  "@media print": { fontSize: "10px" },
};
